import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import LanguageToggle from "../components/LanguageToggle";
import Logo from "../components/Logo";
import { LOAN_PROGRAMS } from "../lib/constants";
import { useT } from "../lib/i18n";
import { ShieldCheck, Rocket, TrendingUp, ArrowUpRight } from "lucide-react";

export default function Landing() {
  const { t } = useT();
  return (
    <div className="min-h-screen text-slate-50">
      <header className="sticky top-0 z-30 border-b border-white/[0.06] bg-[#0b0e0d]/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-home-link">
            <Logo height={88} />
          </Link>
          <div className="flex items-center gap-2">
            <LanguageToggle />
            <Link to="/login">
              <Button variant="ghost" className="text-slate-200 hover:text-white hover:bg-white/5" data-testid="nav-login-btn">
                {t("landing.brokerLogin")}
              </Button>
            </Link>
            <Link to="/register">
              <Button className="bg-emerald-600 text-white hover:bg-emerald-500 btn-glow" data-testid="nav-register-btn">
                {t("landing.becomeBroker")}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        {/* Layered ambient glow behind hero */}
        <div
          className="pointer-events-none absolute inset-0 -z-10 opacity-50"
          style={{
            backgroundImage:
              "radial-gradient(ellipse 50% 40% at 15% 20%, rgba(16,185,129,0.18), transparent 60%), radial-gradient(ellipse 50% 40% at 85% 60%, rgba(212,175,55,0.10), transparent 60%)",
          }}
        />
        <div
          className="pointer-events-none absolute inset-0 -z-10 opacity-[0.07]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 400'><defs><pattern id='g' width='40' height='40' patternUnits='userSpaceOnUse'><path d='M 40 0 L 0 0 0 40' fill='none' stroke='%23ffffff' stroke-width='0.5'/></pattern></defs><rect width='100%25' height='100%25' fill='url(%23g)'/></svg>\")",
          }}
        />
        <div className="mx-auto max-w-7xl px-6 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="max-w-5xl">
            <div className="mb-8 inline-flex items-center gap-2 rounded-full border border-emerald-500/25 bg-emerald-500/[0.06] px-3 py-1 backdrop-blur-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_2px_rgba(16,185,129,0.6)]" />
              <span className="text-[10px] font-medium uppercase tracking-[0.22em] text-emerald-200/90">{t("brand.tagline")}</span>
            </div>
            <h1
              className="font-display font-medium leading-[0.98] tracking-tight text-5xl sm:text-7xl lg:text-[5.5rem]"
              data-testid="hero-title"
            >
              {t("landing.heroTitle1")}{" "}
              <span className="font-display-italic hero-accent">
                {t("landing.heroTitle2")}
              </span>{" "}
              {t("landing.heroTitle3")}
            </h1>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-300/90 md:text-lg">
              {t("landing.heroBody")}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link to="/register">
                <Button size="lg" className="bg-emerald-600 text-white hover:bg-emerald-500 btn-glow px-7" data-testid="hero-register-btn">
                  {t("landing.ctaOnboard")}
                  <ArrowUpRight className="ml-1.5 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/login">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/15 bg-white/[0.02] text-slate-100 hover:bg-white/[0.05] hover:border-white/25 backdrop-blur-sm"
                  data-testid="hero-login-btn"
                >
                  {t("landing.ctaHaveAccount")}
                </Button>
              </Link>
            </div>
          </div>
        </div>
        <div className="hairline mx-auto max-w-7xl" />
      </section>

      {/* Features */}
      <section className="relative">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-6 py-20 md:grid-cols-3">
          <Feature icon={Rocket} title={t("landing.feature1Title")} body={t("landing.feature1Body")} accent="emerald" />
          <Feature icon={ShieldCheck} title={t("landing.feature2Title")} body={t("landing.feature2Body")} accent="gold" />
          <Feature icon={TrendingUp} title={t("landing.feature3Title")} body={t("landing.feature3Body")} accent="emerald" />
        </div>
        <div className="hairline mx-auto max-w-7xl" />
      </section>

      {/* Programs — bento grid */}
      <section className="mx-auto max-w-7xl px-6 py-24">
        <div className="mb-14 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="mb-3 text-[10px] font-medium uppercase tracking-[0.28em] text-[#d4af37]/80">
              {t("landing.programsOver")}
            </div>
            <h2 className="font-display text-4xl font-medium tracking-tight md:text-6xl">
              {t("landing.programsTitle")}
            </h2>
          </div>
          <Link to="/register" className="hidden md:block">
            <Button variant="outline" className="border-white/15 bg-white/[0.02] text-slate-100 hover:bg-white/[0.05] hover:border-emerald-500/30" data-testid="programs-cta-btn">
              {t("landing.programsCta")}
            </Button>
          </Link>
        </div>
        {/* Bento: first card spans 2 cols on desktop */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4 auto-rows-fr">
          {LOAN_PROGRAMS.map((p, idx) => (
            <Link
              to="/register"
              key={p.key}
              className={`group relative overflow-hidden rounded-xl border border-white/[0.06] bg-[#151917]/60 p-6 transition-all duration-300 hover:border-emerald-500/40 hover:bg-[#171c19]/70 hover:-translate-y-0.5 ${
                idx === 0 ? "lg:col-span-2 lg:row-span-1" : ""
              }`}
              data-testid={`program-card-${p.key}`}
            >
              {/* glow on hover */}
              <span className="pointer-events-none absolute -top-12 -right-12 h-40 w-40 rounded-full bg-emerald-500/0 blur-3xl transition-all duration-500 group-hover:bg-emerald-500/15" />
              <div className="relative">
                <div className="mb-5 flex items-baseline justify-between">
                  <div className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-300/90">{p.rate}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Starting</div>
                </div>
                <div className={`font-display ${idx === 0 ? "text-3xl md:text-4xl" : "text-2xl"} leading-tight`}>
                  {p.name}
                </div>
                <div className="mt-3 text-sm leading-relaxed text-slate-400">{p.tagline}</div>
                <div className="mt-6 inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-widest text-slate-500 transition-colors group-hover:text-emerald-300">
                  Explore <ArrowUpRight className="h-3 w-3" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA strip */}
      <section className="relative mx-auto max-w-7xl px-6 pb-24">
        <div className="relative overflow-hidden rounded-2xl border border-white/[0.08] bg-gradient-to-br from-[#151917] to-[#0d1110] p-10 md:p-14">
          <div
            className="pointer-events-none absolute inset-0 opacity-50"
            style={{
              backgroundImage:
                "radial-gradient(ellipse 50% 80% at 90% 100%, rgba(212,175,55,0.18), transparent 60%), radial-gradient(ellipse 60% 80% at 10% 0%, rgba(16,185,129,0.18), transparent 60%)",
            }}
          />
          <div className="relative flex flex-wrap items-center justify-between gap-6">
            <div className="max-w-2xl">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#d4af37]">Ready when you are</div>
              <h3 className="mt-3 font-display text-3xl md:text-4xl leading-tight">
                Join the brokers running their pipeline on PMB Loans.
              </h3>
            </div>
            <Link to="/register">
              <Button size="lg" className="bg-emerald-600 text-white hover:bg-emerald-500 btn-glow px-7" data-testid="cta-register-btn">
                Create broker account
                <ArrowUpRight className="ml-1.5 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/[0.06]">
        <div className="mx-auto max-w-7xl px-6 py-8 text-center text-xs text-slate-500">
          © {new Date().getFullYear()} {t("brand.name")} · {t("landing.footer")}
        </div>
      </footer>
    </div>
  );
}

function Feature({ icon: Icon, title, body, accent }) {
  const accents =
    accent === "gold"
      ? "border-[#d4af37]/25 bg-[#d4af37]/[0.07] text-[#e5c560]"
      : "border-emerald-500/25 bg-emerald-500/[0.07] text-emerald-300";
  return (
    <div className="group relative rounded-xl border border-white/[0.06] bg-[#151917]/50 p-6 transition-all duration-300 hover:border-white/[0.12] hover:bg-[#171c19]/70">
      <div className={`mb-5 flex h-11 w-11 items-center justify-center rounded-lg border ${accents}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="font-display text-2xl leading-tight text-slate-100">{title}</div>
      <div className="mt-2 text-sm leading-relaxed text-slate-400">{body}</div>
    </div>
  );
}
